// src/components/PreClearanceDialog.js
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { searchClearingOfficer, submitPreClearance } from '../services/verificationService';

const PreClearanceDialog = ({ open, personnel, onClose, onSuccess }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [clearingOfficer, setClearingOfficer] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchError('Please enter Service Number, IPPIS Number, or Account Number');
      return;
    }

    setSearching(true);
    setSearchError('');
    setClearingOfficer(null);

    try {
      const response = await searchClearingOfficer(searchQuery.trim(), personnel.commandId);
      setClearingOfficer(response.data.clearingOfficer);
      setSearchError('');
    } catch (err) {
      setSearchError(err.response?.data?.message || 'Clearing officer not found in this command');
      setClearingOfficer(null);
    } finally {
      setSearching(false);
    }
  };

  const handleSubmit = async () => {
    if (!clearingOfficer) {
      setError('Please search and select a clearing officer first');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // FIX: Pass clearingOfficer.id (UUID) as the second argument,
      // then serviceNumber, then name, then notes
      await submitPreClearance(
        personnel.id,
        clearingOfficer.id,                // ← Personnel UUID (foreign key)
        clearingOfficer.serviceNumber,      // ← Service number string (for notes/audit)
        `${clearingOfficer.firstName} ${clearingOfficer.lastName} (${clearingOfficer.rankName})`,
        notes || undefined
      );

      handleClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Pre-clearance error:', err);
      setError(err.response?.data?.message || 'Failed to submit pre-clearance');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setSearchQuery('');
      setClearingOfficer(null);
      setNotes('');
      setSearchError('');
      setError('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Record Pre-Clearance</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Personnel Info */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Personnel to be Cleared:
            </Typography>
            <Typography variant="h6">
              {personnel.serviceNumber} - {personnel.firstName} {personnel.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {personnel.rankName} • {personnel.command.name}
            </Typography>
          </Paper>

          {/* Search Clearing Officer */}
          <Typography variant="subtitle1" gutterBottom>
            Search for Clearing Officer
          </Typography>
          <Typography variant="caption" color="text.secondary" gutterBottom display="block" sx={{ mb: 2 }}>
            Search for the Comptroller/Principal who cleared this officer (by Service Number, IPPIS Number, or Account Number)
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="Service Number, IPPIS Number, or Account Number"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="e.g., 56334 or NI9001234 or 0123456789"
              disabled={searching || submitting}
            />
            <Button
              variant="contained"
              startIcon={searching ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
              onClick={handleSearch}
              disabled={searching || submitting}
              sx={{ minWidth: 120 }}
            >
              {searching ? 'Searching...' : 'Search'}
            </Button>
          </Box>

          {searchError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {searchError}
            </Alert>
          )}

          {/* Clearing Officer Found */}
          {clearingOfficer && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'success.lighter', borderColor: 'success.main' }}>
              <Typography variant="subtitle2" color="success.dark" gutterBottom>
                ✅ Clearing Officer Found
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Service Number</Typography>
                  <Typography variant="body1" fontWeight="600">{clearingOfficer.serviceNumber}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Full Name</Typography>
                  <Typography variant="body1" fontWeight="600">
                    {clearingOfficer.firstName} {clearingOfficer.lastName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Rank</Typography>
                  <Typography variant="body1">{clearingOfficer.rankName}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Command</Typography>
                  <Typography variant="body1">{clearingOfficer.command.name}</Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Notes */}
          <TextField
            fullWidth
            label="Notes (Optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={3}
            placeholder="Any additional information about this pre-clearance..."
            disabled={submitting}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!clearingOfficer || submitting}
          startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {submitting ? 'Submitting...' : 'Confirm Pre-Clearance'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreClearanceDialog;